<template>
  <div>
    <v-container fluid>
      <v-card elevation="3" rounded="xl">
        <v-card-title class="py-4 px-6">
          <v-row class="w-100 ma-0" align="center">
            <v-col cols="12" md="6" class="pa-0">
              <h2 class="text-h5 font-weight-bold primary--text">RMC Plans</h2>
              <p class="text-body-2 grey--text ma-0">Manage membership plans shown in the app</p>
            </v-col>
            <v-col cols="12" md="6" class="pa-0 text-right">
              <v-chip color="primary" outlined class="mr-3">
                <v-icon left small>mdi-layers</v-icon>
                {{ plans.length }} Plans
              </v-chip>
              <v-btn color="primary" rounded @click="openAdd">
                <v-icon left small>mdi-plus</v-icon>
                Add Plan
              </v-btn>
            </v-col>
          </v-row>
        </v-card-title>

        <v-divider class="mx-6"></v-divider>

        <v-card-text class="pa-0">
          <section v-if="isLoading" style="height:300px" class="d-flex align-center justify-center">
            <v-progress-circular indeterminate color="primary" size="50"></v-progress-circular>
          </section>

          <v-data-table v-else :headers="headers" :items="plans" hide-default-footer class="enhanced-table">
            <template #[`item.sr_no`]="{ index }">
              <span class="font-weight-medium primary--text">{{ index + 1 }}</span>
            </template>

            <template #[`item.name`]="{ item }">
              <div>
                <div class="font-weight-medium">{{ item.name }}</div>
                <div class="text-caption grey--text">{{ item.description }}</div>
              </div>
            </template>

            <template #[`item.badge`]="{ item }">
              <v-chip small :color="item.is_popular ? 'amber darken-2' : 'teal'" dark>
                {{ item.badge || '—' }}
              </v-chip>
            </template>

            <template #[`item.booking_type`]="{ item }">
              <v-chip small :color="item.booking_type === 'on_demand' ? 'purple' : 'blue'" dark outlined>
                {{ item.booking_type === 'on_demand' ? 'On-Demand' : 'Dedicated' }}
              </v-chip>
            </template>

            <template #[`item.price_monthly`]="{ item }">
              <span class="font-weight-medium">₹{{ item.price_monthly }}/m³</span>
            </template>

            <template #[`item.price_annual`]="{ item }">
              <span class="font-weight-medium">₹{{ item.price_annual }}/m³</span>
            </template>

            <template #[`item.duration_days`]="{ item }">
              <span>{{ item.duration_days ? item.duration_days + ' days' : 'End of month' }}</span>
            </template>

            <template #[`item.is_popular`]="{ item }">
              <v-icon :color="item.is_popular ? 'amber darken-2' : 'grey lighten-2'">
                {{ item.is_popular ? 'mdi-star' : 'mdi-star-outline' }}
              </v-icon>
            </template>

            <template #[`item.is_active`]="{ item }">
              <v-chip small :color="item.is_active ? 'success' : 'error'" dark>
                {{ item.is_active ? 'Active' : 'Inactive' }}
              </v-chip>
            </template>

            <template #[`item.actions`]="{ item }">
              <v-btn icon small @click="openEdit(item)">
                <v-icon small color="primary">mdi-pencil</v-icon>
              </v-btn>
              <v-btn icon small @click="deletePlan(item)">
                <v-icon small color="error">mdi-delete</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>

      <!-- Add/Edit Dialog -->
      <v-dialog v-model="dialog" max-width="700" persistent>
        <v-card rounded="xl">
          <v-card-title class="py-4 px-6 primary white--text">
            {{ editingPlan ? 'Edit Plan' : 'Add Plan' }}
            <v-spacer></v-spacer>
            <v-btn icon dark @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
          </v-card-title>

          <v-card-text class="pt-6 px-6">
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field v-model="form.name" label="Plan Name *" outlined dense placeholder="e.g. Co-Manufacturing"></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="form.badge" label="Badge Text" outlined dense placeholder="e.g. FLEX or MOST POPULAR ⭐"></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-select v-model="form.booking_type" :items="bookingTypes" label="Booking Type *" outlined dense></v-select>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="form.accent_color" label="Accent Color (hex)" outlined dense placeholder="#10B981"></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-textarea v-model="form.description" label="Description" outlined dense rows="2" auto-grow></v-textarea>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field v-model.number="form.price_monthly" label="Price Monthly (₹/m³) *" type="number" outlined dense></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field v-model.number="form.price_annual" label="Price Annual (₹/m³)" type="number" outlined dense hint="Leave 0 to auto-calculate (15% off monthly)"></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field v-model.number="form.retainer_amount" label="Retainer Amount (₹)" type="number" outlined dense></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field v-model.number="form.min_m3" label="Min. m³ per Booking" type="number" outlined dense hint="0 = no minimum (On-Demand)"></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field v-model.number="form.quota_m3" label="Quota m³ per Period" type="number" outlined dense hint="Total m³ allowed per subscription period. 0 = unlimited"></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field v-model.number="form.duration_days" label="Plan Duration (days)" type="number" outlined dense hint="e.g. 30 = 30-day validity. Leave 0 = end of month."></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field v-model.number="form.sort_order" label="Sort Order" type="number" outlined dense hint="Lower = shown first"></v-text-field>
              </v-col>
              <v-col cols="12" md="2" class="d-flex align-center">
                <v-switch v-model="form.is_popular" label="Most Popular" color="amber darken-2" dense hide-details></v-switch>
              </v-col>
              <v-col cols="12" md="2" class="d-flex align-center">
                <v-switch v-model="form.is_active" label="Active" color="success" dense hide-details></v-switch>
              </v-col>

              <!-- Features -->
              <v-col cols="12">
                <div class="d-flex align-center justify-space-between mb-2">
                  <span class="text-subtitle-2 font-weight-bold">Included Features</span>
                  <v-btn small text color="primary" @click="addFeature">
                    <v-icon small left>mdi-plus</v-icon> Add Feature
                  </v-btn>
                </div>
                <v-row v-for="(feature, i) in form.features" :key="i" dense class="mb-1">
                  <v-col cols="5">
                    <v-text-field v-model="feature.label" label="Label" outlined dense hide-details placeholder="e.g. Min. Commitment"></v-text-field>
                  </v-col>
                  <v-col cols="6">
                    <v-text-field v-model="feature.value" label="Value" outlined dense hide-details placeholder="e.g. 500 m³/mo"></v-text-field>
                  </v-col>
                  <v-col cols="1" class="d-flex align-center">
                    <v-btn icon small @click="removeFeature(i)"><v-icon small color="error">mdi-close</v-icon></v-btn>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-card-text>

          <v-card-actions class="px-6 pb-4">
            <v-spacer></v-spacer>
            <v-btn text @click="dialog = false">Cancel</v-btn>
            <v-btn color="primary" rounded :loading="saving" @click="savePlan">
              {{ editingPlan ? 'Update Plan' : 'Create Plan' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Snackbar -->
      <v-snackbar v-model="snackbar.show" :color="snackbar.color" top right>
        {{ snackbar.message }}
      </v-snackbar>
    </v-container>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "RmcPlansPage",
  data() {
    return {
      plans: [],
      isLoading: false,
      dialog: false,
      saving: false,
      editingPlan: null,
      snackbar: { show: false, message: "", color: "success" },
      headers: [
        { text: "#", value: "sr_no", sortable: false, width: 50 },
        { text: "Plan Name", value: "name", sortable: false },
        { text: "Badge", value: "badge", sortable: false },
        { text: "Type", value: "booking_type", sortable: false },
        { text: "Monthly", value: "price_monthly", sortable: false },
        { text: "Annual", value: "price_annual", sortable: false },
        { text: "Duration", value: "duration_days", sortable: false },
        { text: "Popular", value: "is_popular", sortable: false, align: "center" },
        { text: "Status", value: "is_active", sortable: false },
        { text: "Actions", value: "actions", sortable: false, align: "center" },
      ],
      bookingTypes: [
        { text: "On-Demand", value: "on_demand" },
        { text: "Dedicated Co-Manufacturing", value: "dedicated" },
      ],
      form: this.defaultForm(),
    };
  },
  mounted() {
    this.getData();
  },
  methods: {
    defaultForm() {
      return {
        name: "",
        badge: "",
        booking_type: "on_demand",
        description: "",
        price_monthly: 0,
        price_annual: 0,
        retainer_amount: 0,
        min_m3: 0,
        quota_m3: 0,
        duration_days: 0,
        is_popular: false,
        is_active: true,
        accent_color: "#5B2EFF",
        sort_order: 0,
        features: [],
      };
    },
    async getData() {
      this.isLoading = true;
      try {
        const res = await axios.get(`rmc_plans`);
        this.plans = res.data.data || [];
      } catch (e) {
        this.showSnack("Failed to load plans", "error");
      } finally {
        this.isLoading = false;
      }
    },
    openAdd() {
      this.editingPlan = null;
      this.form = this.defaultForm();
      this.dialog = true;
    },
    openEdit(plan) {
      this.editingPlan = plan;
      this.form = {
        name: plan.name,
        badge: plan.badge || "",
        booking_type: plan.booking_type,
        description: plan.description || "",
        price_monthly: plan.price_monthly,
        price_annual: plan.price_annual,
        retainer_amount: plan.retainer_amount,
        min_m3: plan.min_m3 || 0,
        quota_m3: plan.quota_m3 || 0,
        duration_days: plan.duration_days || 0,
        is_popular: plan.is_popular,
        is_active: plan.is_active,
        accent_color: plan.accent_color || "#5B2EFF",
        sort_order: plan.sort_order || 0,
        features: plan.features ? [...plan.features] : [],
      };
      this.dialog = true;
    },
    addFeature() {
      this.form.features.push({ label: "", value: "" });
    },
    removeFeature(i) {
      this.form.features.splice(i, 1);
    },
    async savePlan() {
      if (!this.form.name || !this.form.booking_type || !this.form.price_monthly) {
        this.showSnack("Name, Booking Type and Monthly Price are required", "error");
        return;
      }
      // Auto-calculate annual if left at 0
      if (!this.form.price_annual || this.form.price_annual === 0) {
        this.form.price_annual = parseFloat((this.form.price_monthly * 0.85).toFixed(2));
      }
      this.saving = true;
      try {
        if (this.editingPlan) {
          await axios.patch(`rmc_plans/${this.editingPlan.id}`, this.form);
          this.showSnack("Plan updated successfully");
        } else {
          await axios.post(`rmc_plans`, this.form);
          this.showSnack("Plan created successfully");
        }
        this.dialog = false;
        this.getData();
      } catch (e) {
        this.showSnack("Error saving plan", "error");
      } finally {
        this.saving = false;
      }
    },
    async deletePlan(plan) {
      if (!confirm(`Delete plan "${plan.name}"?`)) return;
      try {
        await axios.delete(`rmc_plans/${plan.id}`);
        this.showSnack("Plan deleted");
        this.getData();
      } catch (e) {
        this.showSnack("Error deleting plan", "error");
      }
    },
    showSnack(message, color = "success") {
      this.snackbar = { show: true, message, color };
    },
  },
};
</script>
